import z from "zod";
import { SourceMetadataListSchema } from "../source-metadata.schema.js";
export const JiraCommentSchema = z.object({
    id: z.string(),
    parentCommentId: z.string().optional(),
    author: z.string().nullable().optional(),
    body: z.string(),
    created: z.string().optional(),
    updated: z.string().optional(),
});
export const JiraAttachmentSchema = z.object({
    id: z.string(),
    filename: z.string(),
    url: z.string().url(),
});
export const JiraLinkedIssueSchema = z.object({
    key: z.string(),
    summary: z.string().optional(),
    linkType: z.string().optional(),
    direction: z.enum(["inward", "outward"]).optional(),
});
export const JiraIssueParentSchema = z.object({
    key: z.string(),
    summary: z.string().nullable().optional(),
    issueType: z.string().nullable().optional(),
});
export const JiraIssueSchema = z.object({
    key: z.string(),
    summary: z.string(),
    status: z.string(),
    issueType: z.string().nullable().optional(),
    storyPoints: z.number().nullable().optional(),
    acceptanceCriteria: z.string().nullable().optional(),
    assignee: z.string().nullable().optional(),
    priority: z.string().nullable().optional(),
    labels: z.array(z.string()).optional(),
    updated: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    dueDate: z.string().nullable().optional(),
    parent: JiraIssueParentSchema.nullable().optional(),
    url: z.string().url(),
    attachments: z.array(JiraAttachmentSchema).optional(),
    linkedIssues: z.array(JiraLinkedIssueSchema).optional(),
    comments: z.array(JiraCommentSchema).optional(),
});
export const JiraGetIssueInputSchema = z.object({
    issueKey: z.string().min(2, "Issue key is required"),
});
export const JiraGetIssueOutputSchema = z.object({
    source: z.literal("Jira"),
    found: z.boolean(),
    issue: JiraIssueSchema.nullable(),
    sources: SourceMetadataListSchema.optional(),
    message: z.string().optional(),
});
export const JiraSearchInputSchema = z.object({
    query: z.string().min(1, "Search query is required"),
    projectKey: z
        .string()
        .optional()
        .transform((value) => {
        const trimmed = value?.trim();
        return trimmed ? trimmed : undefined;
    }),
    maxResults: z.number().int().min(1).max(200).optional(),
    fields: z.array(z.string().min(1)).optional(),
});
export const JiraSearchOutputSchema = z.object({
    source: z.literal("Jira"),
    jql: z.string(),
    count: z.number().int(),
    total: z.number().int().min(0),
    truncated: z.boolean(),
    issues: z.array(JiraIssueSchema),
    queryMode: z.enum(["jql", "natural_language"]).optional(),
    sources: SourceMetadataListSchema.optional(),
});
export const JiraListProjectMembersInputSchema = z.object({
    projectKey: z
        .string()
        .optional()
        .transform((value) => {
        const trimmed = value?.trim();
        return trimmed ? trimmed : undefined;
    })
        .describe("Ключ проекта Jira. Если неизвестен — оставь пустым: будет использован проект workspace."),
    maxResults: z.number().int().min(1).max(200).default(100).optional(),
});
export const JiraProjectMemberSchema = z.object({
    accountId: z.string(),
    displayName: z.string(),
    roles: z.array(z.string()),
    avatarUrl: z.string().url().nullable().optional(),
});
export const JiraListProjectMembersOutputSchema = z.object({
    source: z.literal("Jira"),
    projectKey: z.string(),
    count: z.number().int().min(0),
    total: z.number().int().min(0),
    members: z.array(JiraProjectMemberSchema),
    sources: SourceMetadataListSchema.optional(),
    message: z.string().optional(),
});
export const JiraAddCommentInputSchema = z.object({
    issueKey: z.string().min(2, "Issue key is required"),
    body: z.string().min(1, "Comment text is required"),
    author: z.string().trim().min(1, "Comment author is required"),
    parentCommentId: z
        .string()
        .trim()
        .min(1, "Parent comment id is required")
        .optional()
        .describe("ID комментария в этой задаче, на который нужно ответить. Ответ будет опубликован как цитата исходного комментария."),
});
export const JiraAddCommentOutputSchema = z.object({
    source: z.literal("Jira"),
    result: z.object({
        ok: z.literal(true),
        issueKey: z.string(),
        commentId: z.string(),
        parentCommentId: z.string().optional(),
    }),
});
export const JiraGetCommentsInputSchema = z.object({
    issueKey: z.string().min(2, "Issue key is required"),
    limit: z.number().int().min(1).max(50).optional(),
});
export const JiraGetCommentsOutputSchema = z.object({
    source: z.literal("Jira"),
    issueKey: z.string(),
    comments: z.array(JiraCommentSchema),
    sources: SourceMetadataListSchema.optional(),
});
export const JiraCreateIssueInputSchema = z.object({
    projectKey: z
        .string()
        .optional()
        .describe('Ключ проекта Jira (например "LP", "KAN") — короткий код из латинских букв/цифр без пробелов. ' +
        "НЕ придумывай и не пиши название проекта словами. " +
        "Если ключ неизвестен — оставь поле пустым: будет использован проект, настроенный в воркспейсе."),
    summary: z.string().min(1, "Summary is required"),
    description: z.string().optional(),
    issueType: z.string().min(1, "Issue type is required"),
    priority: z.string().optional(),
    assignee: z.string().optional(),
    labels: z.array(z.string()).optional(),
    fixVersions: z
        .array(z.string().min(1))
        .optional()
        .describe('Названия версий поля "Fix Version/s" (например ["1.2.0"]). ' +
        "Версии должны уже существовать в проекте — новые версии не создаются."),
    storyPoints: z
        .number()
        .optional()
        .describe("Оценка в стори-поинтах (число). Поле Story Points / Story point estimate."),
    parentKey: z
        .string()
        .optional()
        .describe('Ключ родительской задачи (например "LP-80"). Используй и для эпика, ' +
        "и для родителя подзадачи: Jira хранит обе связи в fields.parent."),
    addToActiveSprint: z
        .boolean()
        .optional()
        .describe("Поставь true, если пользователь просит создать задачу в текущем/активном спринте. " +
        "Задача будет добавлена в активный спринт доски проекта после создания."),
});
const JiraSprintRefSchema = z.object({
    id: z.number(),
    name: z.string(),
});
export const JiraCreateSprintInputSchema = z
    .object({
    projectKey: z
        .string()
        .optional()
        .describe("Ключ проекта Jira. Если неизвестен — оставь пустым: будет использован проект workspace."),
    boardId: z
        .number()
        .int()
        .positive()
        .optional()
        .describe("ID Scrum-доски. Передавай только если у проекта несколько Scrum-досок и пользователь выбрал нужную."),
    name: z.string().trim().min(1, "Sprint name is required"),
    goal: z.string().trim().min(1).optional(),
    issueKeys: z
        .array(z.string().trim().min(2, "Issue key is required"))
        .max(50, "Jira can move at most 50 issues to a sprint at once")
        .default([]),
    startDate: z
        .string()
        .datetime({ offset: true })
        .optional()
        .describe("Дата начала спринта в ISO 8601 с часовым поясом."),
    endDate: z
        .string()
        .datetime({ offset: true })
        .optional()
        .describe("Дата окончания спринта в ISO 8601 с часовым поясом."),
    startSprint: z
        .boolean()
        .default(false)
        .describe("Поставь true только если пользователь явно просит сразу запустить спринт."),
})
    .superRefine((value, ctx) => {
    if (value.startSprint && (!value.startDate || !value.endDate)) {
        ctx.addIssue({
            code: "custom",
            message: "startDate and endDate are required when startSprint is true",
            path: [value.startDate ? "endDate" : "startDate"],
        });
    }
    if (value.startDate &&
        value.endDate &&
        Date.parse(value.endDate) <= Date.parse(value.startDate)) {
        ctx.addIssue({
            code: "custom",
            message: "endDate must be later than startDate",
            path: ["endDate"],
        });
    }
});
export const JiraSprintSchema = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    state: z.enum(["future", "active"]),
    boardId: z.number().int().positive(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    goal: z.string().optional(),
});
export const JiraListSprintsInputSchema = z.object({
    projectKey: z
        .string()
        .optional()
        .describe("Ключ проекта Jira. Если неизвестен — оставь пустым: будет использован проект workspace."),
    boardId: z
        .number()
        .int()
        .positive()
        .optional()
        .describe("Необязательный ID конкретной Scrum-доски."),
    state: z
        .enum(["current", "active", "future", "closed", "all"])
        .default("current")
        .describe("current возвращает активные и будущие спринты. Используй closed/all только по явной просьбе пользователя."),
});
export const JiraSprintSummarySchema = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    state: z.enum(["future", "active", "closed"]),
    boardId: z.number().int().positive(),
    boardName: z.string(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    completeDate: z.string().optional(),
    goal: z.string().optional(),
});
export const JiraListSprintsOutputSchema = z.object({
    source: z.literal("Jira"),
    projectKey: z.string(),
    count: z.number().int().min(0),
    boards: z.array(z.object({
        id: z.number().int().positive(),
        name: z.string(),
    })),
    sprints: z.array(JiraSprintSummarySchema),
});
export const JiraCreateSprintOutputSchema = z.object({
    source: z.literal("Jira"),
    ok: z.boolean(),
    sprint: JiraSprintSchema,
    issueKeys: z.array(z.string()),
    issuesAdded: z.number().int().min(0),
    message: z.string().optional(),
});
export const JiraMoveIssuesToSprintInputSchema = z.object({
    sprintId: z
        .number()
        .int()
        .positive()
        .describe("ID существующего active или future спринта из jira-list-sprints."),
    issueKeys: z
        .array(z.string().trim().min(2, "Issue key is required"))
        .min(1, "At least one issue key is required")
        .max(50, "Jira can move at most 50 issues to a sprint at once"),
});
export const JiraMoveIssuesToSprintOutputSchema = z.object({
    source: z.literal("Jira"),
    ok: z.literal(true),
    sprintId: z.number().int().positive(),
    issueKeys: z.array(z.string()),
    issuesMoved: z.number().int().min(1),
});
export const JiraCreateIssueOutputSchema = z.object({
    source: z.literal("Jira"),
    issueKey: z.string(),
    url: z.string().url(),
    sprint: JiraSprintRefSchema.nullable().optional(),
    sources: SourceMetadataListSchema.optional(),
});
export const JiraEpicChildDraftSchema = z.object({
    summary: z.string().min(1, "Summary is required"),
    description: z.string().optional(),
    issueType: z.string().optional(),
    priority: z.string().optional(),
    assignee: z.string().optional(),
    labels: z.array(z.string()).optional(),
    fixVersions: z.array(z.string().min(1)).optional(),
    storyPoints: z.number().optional(),
});
export const JiraEpicChildIssueSpecSchema = JiraEpicChildDraftSchema.extend({
    issueType: z.string().min(1, "Issue type is required").default("Task"),
});
const JiraBuilderIssueFieldsSchema = z
    .object({
    summary: z.string().nullish(),
    description: z.string().nullish(),
    issuetype: z.object({ name: z.string().nullish() }).nullish(),
    priority: z.object({ name: z.string().nullish() }).nullish(),
    labels: z.array(z.string().nullish()).nullish(),
    fixVersions: z
        .array(z.union([
        z.string().nullish(),
        z.object({ name: z.string().nullish() }).passthrough(),
    ]))
        .nullish(),
})
    .passthrough();
export const JiraEpicChildIssueBuilderOutputSchema = z
    .object({
    summary: z.string().nullish(),
    description: z.string().nullish(),
    issueType: z.string().nullish(),
    priority: z
        .union([z.string(), z.object({ name: z.string().nullish() })])
        .nullish(),
    assignee: z.string().nullish(),
    labels: z.array(z.string().nullish()).nullish(),
    fixVersions: z
        .array(z.union([
        z.string().nullish(),
        z.object({ name: z.string().nullish() }).passthrough(),
    ]))
        .nullish(),
    storyPoints: z.number().nullish(),
    fields: JiraBuilderIssueFieldsSchema.nullish(),
})
    .passthrough();
export const JiraDecomposeEpicInputSchema = z.object({
    parentKey: z.string().min(2, "Parent key is required"),
    projectKey: z
        .string()
        .optional()
        .describe("Ключ проекта Jira. Если неизвестен — оставь пустым: будет использован проект workspace."),
    items: z
        .array(JiraEpicChildDraftSchema)
        .min(1, "At least one child issue is required")
        .max(50, "Too many child issues in one request"),
    defaults: JiraEpicChildDraftSchema.partial().optional(),
    concurrency: z.number().int().min(1).max(5).default(3).optional(),
    maxRetries: z.number().int().min(0).max(5).default(2).optional(),
});
export const JiraDecomposeEpicCreatedIssueSchema = z.object({
    index: z.number().int().min(0),
    summary: z.string(),
    issueKey: z.string(),
    url: z.string().url(),
    attempts: z.number().int().min(1),
});
export const JiraDecomposeEpicFailedIssueSchema = z.object({
    index: z.number().int().min(0),
    summary: z.string(),
    reason: z.string(),
    errorType: z.enum(["validation", "transient", "unknown"]),
    attempts: z.number().int().min(1),
});
export const JiraDecomposeEpicOutputSchema = z.object({
    source: z.literal("Jira"),
    parentKey: z.string(),
    created: z.array(JiraDecomposeEpicCreatedIssueSchema),
    failed: z.array(JiraDecomposeEpicFailedIssueSchema),
    message: z.string().optional(),
});
export const JiraUpdateIssueFieldsSchema = z.object({
    summary: z.string().min(1).optional(),
    description: z.string().optional(),
    priority: z.string().optional(),
    labels: z.array(z.string()).optional(),
    dueDate: z.string().optional(),
    fixVersions: z
        .array(z.string().min(1))
        .optional()
        .describe('Названия версий поля "Fix Version/s", которые нужно проставить задаче ' +
        '(например ["1.2.0"]). Версии должны уже существовать в проекте — ' +
        "новые версии этот инструмент не создаёт. Передай пустой массив [], " +
        "чтобы очистить поле."),
    storyPoints: z
        .number()
        .optional()
        .describe("Оценка в стори-поинтах (число). Поле Story Points / Story point estimate."),
});
export const JiraUpdateIssueInputSchema = z
    .object({
    issueKey: z.string().min(2, "Issue key is required"),
    fields: JiraUpdateIssueFieldsSchema.optional(),
    parentKey: z
        .string()
        .min(2, "Parent key is required")
        .optional()
        .describe('Ключ родительской задачи (например "LP-80"). Для современной Jira Cloud ' +
        "это основной способ назначить задаче эпик через поле parent."),
    removeParent: z
        .boolean()
        .optional()
        .describe("Поставь true, когда пользователь явно просит убрать родителя из задачи. " +
        "Поле parent будет очищено."),
    addToActiveSprint: z
        .boolean()
        .optional()
        .describe("Поставь true, чтобы добавить эту задачу в текущий/активный спринт её проекта. " +
        "Можно вызвать без fields, только для перемещения в спринт."),
})
    .superRefine((value, ctx) => {
    if (value.parentKey && value.removeParent) {
        ctx.addIssue({
            code: "custom",
            message: "A parent cannot be set and removed in the same request",
            path: ["removeParent"],
        });
    }
});
export const JiraUpdateIssueOutputSchema = z.object({
    source: z.literal("Jira"),
    ok: z.literal(true),
    issueKey: z.string(),
    sprint: JiraSprintRefSchema.nullable().optional(),
    parentKey: z.string().nullable().optional(),
    removedParentKey: z.string().nullable().optional(),
});
export const JiraTransitionIssueInputSchema = z.object({
    issueKey: z.string().min(2, "Issue key is required"),
    targetStatus: z.string().min(1, "Target status is required"),
});
export const JiraTransitionIssueOutputSchema = z.object({
    source: z.literal("Jira"),
    ok: z.literal(true),
    issueKey: z.string(),
    status: z.string(),
});
export const JiraAssignIssueInputSchema = z.object({
    issueKey: z.string().min(2, "Issue key is required"),
    assignee: z.string().min(1, "Assignee is required"),
});
export const JiraAssignIssueOutputSchema = z.object({
    source: z.literal("Jira"),
    ok: z.literal(true),
    issueKey: z.string(),
    assignee: z.string(),
});
export const JiraLinkIssuesInputSchema = z.object({
    inwardIssue: z
        .string()
        .min(2, "Inward issue key is required")
        .describe("Ключ задачи на «принимающей» стороне связи (получает inward-описание). " +
        'Для типа "Blocks" это задача, которая "is blocked by" другой.'),
    outwardIssue: z
        .string()
        .min(2, "Outward issue key is required")
        .describe("Ключ задачи на «активной» стороне связи (получает outward-описание). " +
        'Для типа "Blocks" это задача, которая "blocks" другую.'),
    linkType: z
        .string()
        .min(1, "Link type is required")
        .describe('Имя типа связи Jira: "Blocks", "Relates", "Duplicate", "Cloners" и т.п. ' +
        'Можно указать также inward/outward-описание (например "is blocked by", ' +
        '"blocks") — будет сопоставлено с доступными типами связей.'),
});
export const JiraLinkIssuesOutputSchema = z.object({
    source: z.literal("Jira"),
    ok: z.literal(true),
    inwardIssue: z.string(),
    outwardIssue: z.string(),
    linkType: z.string(),
});
export const JiraProjectSummarySchema = z.object({
    id: z.string(),
    key: z.string(),
    name: z.string(),
});
export const JiraListProjectsInputSchema = z.object({});
export const JiraListProjectsOutputSchema = z.object({
    source: z.literal("Jira"),
    projects: z.array(JiraProjectSummarySchema),
});
//# sourceMappingURL=jira.schema.js.map